import { Box, Select, SelectContent, SelectTrigger, Tooltip } from '@optiaxiom/react';

import { TurnstileInstance } from '@frontend/api/types';
import { Product } from '@frontend/contexts/connectionUtils';

type ProductInstanceSelectorProps = {
  products: Record<string, Product>;
  value?: string;
  onValueChange: (instance: TurnstileInstance | undefined) => void;
  className?: string;
  placeholder?: string;
  tooltipContent?: string;
};

export function ProductInstanceSelector({
  products,
  value,
  onValueChange,
  className,
  placeholder = 'Set Product Instance',
  tooltipContent = 'Some Opal capabilities depend on specific Optimizely products. Switch here for Opal to take action in the selected product.',
}: ProductInstanceSelectorProps) {
  const CLEAR_VALUE = '__clear_selection__';

  const clearGroup = {
    label: '',
    separator: true,
  };

  const groups = Object.keys(products).reduce(
    (acc, label) => {
      acc[label] = {
        label,
        separator: true,
      };
      return acc;
    },
    {} as Record<string, { label: string; separator: boolean }>
  );

  const productInstances = Object.entries(products).flatMap(([groupLabel, groupItems]) => {
    return groupItems.instances.map(instance => ({
      group: groups[groupLabel],
      label: instance.nickname ?? instance.name,
      value: instance.id,
    }));
  });

  const instanceSelectOptions = [
    ...(value
      ? [
          {
            label: 'Clear selection',
            value: CLEAR_VALUE,
            group: clearGroup,
          },
        ]
      : []),
    ...productInstances,
  ];

  const handleValueChange = (instanceId: string) => {
    if (instanceId === CLEAR_VALUE) {
      onValueChange(undefined);
      return;
    }

    const selectedInstance = Object.values(products)
      .flatMap(group => group.instances)
      .find(instance => instance.id === instanceId);
    onValueChange(selectedInstance);
  };

  const selectProps = value
    ? { value, options: instanceSelectOptions, onValueChange: handleValueChange }
    : { value: CLEAR_VALUE, options: instanceSelectOptions, onValueChange: handleValueChange };

  return (
    <Tooltip content={tooltipContent}>
      <Box className={className}>
        <Select {...selectProps}>
          <SelectTrigger placeholder={placeholder} w="fit" />
          <SelectContent />
        </Select>
      </Box>
    </Tooltip>
  );
}
